import { useEffect, useRef, useState } from "react";
import { useViewer } from "../../../../state/ViewerProvider";
import type { GltfAnimationInfo, GltfPlaybackState } from "../../../../shared/api-contract";
import { CompactCheck, CompactSelect, CompactSlider, MiniBtn } from "../../inspector/rows";

const EMPTY: GltfPlaybackState = {
  available: false, clipCount: 0, index: -1, name: null,
  playing: false, time: 0, duration: 0, loop: true, speed: 1,
  timeScale: 1, animateOnScroll: false, animateOnDrag: false, autoplayOnLoad: false, syncMaxDuration: false,
  autoIncrementTime: false, dragAxis: "x", dragAnimationDamping: 0.1, scrollAnimationDamping: 0.1,
};

/**
 * Baked GLTF clip playback (v15). Lists the loaded model's clips, drives the
 * AnimationMixer over RPC, and stays live via the `animationStateChanged`
 * event (no 200 ms poll — only the time readout polls while playing).
 */
export function GltfAnimationControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [clips, setClips] = useState<GltfAnimationInfo[]>([]);
  const [state, setState] = useState<GltfPlaybackState>(EMPTY);
  const [err, setErr] = useState<string | null>(null);
  const timePoll = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Every transport/option call below was `void rpc().then(setState)` — on
   * rejection the promise died unhandled and the panel kept rendering the old
   * state, so a clip that refused to play looked like one that simply had not
   * started. Apply the new state on success, surface the reason on failure.
   */
  const apply = (what: string, p: Promise<GltfPlaybackState>) => {
    setErr(null);
    void p.then(setState).catch((e) => setErr(`${what} failed: ${e instanceof Error ? e.message : String(e)}`));
  };

  useEffect(() => {
    let alive = true;
    const refresh = () => {
      r.listGltfAnimations().then((c) => alive && setClips(c)).catch(() => alive && setClips([]));
      r.getGltfPlaybackState().then((s) => alive && setState(s)).catch(() => {});
    };
    refresh();
    const offModels = client.on("modelsChanged", refresh);
    const offAnim = client.on("animationStateChanged", (p) => {
      if (alive && p && (p as { gltf?: GltfPlaybackState }).gltf) setState((p as { gltf: GltfPlaybackState }).gltf);
    });
    return () => { alive = false; offModels(); offAnim(); };
  }, [client, r]);

  // While playing, poll only the time readout (event covers discrete changes).
  useEffect(() => {
    if (state.playing) {
      timePoll.current = setInterval(() => {
        r.getGltfPlaybackState().then((s) => setState((prev) => ({ ...prev, time: s.time, playing: s.playing }))).catch(() => {});
      }, 200);
    }
    return () => { if (timePoll.current) { clearInterval(timePoll.current); timePoll.current = null; } };
  }, [state.playing, r]);

  if (!state.available && clips.length === 0) {
    return <span className="block px-3 py-1.5 text-[11px] text-ed-text-3">This model has no baked animation clips.</span>;
  }

  const activeIdx = state.index >= 0 ? state.index : 0;
  const pct = state.duration > 0 ? Math.round((state.time / state.duration) * 100) : 0;

  return (
    <div className="flex flex-col gap-1.5 px-3 py-1.5">
      <div className="flex items-center gap-2">
        <CompactSelect
          value={String(activeIdx)}
          options={clips.map((c) => ({ id: String(c.index), label: c.name }))}
          onChange={(v) => apply("Select clip", r.playGltfAnimation(Number(v)))}
        />
      </div>
      <div className="flex items-center gap-1.5">
        <MiniBtn primary={!state.playing} onClick={() => apply("Play", r.playGltfAnimation(activeIdx))} icon="play_arrow">Play</MiniBtn>
        <MiniBtn onClick={() => apply("Pause", r.pauseGltfAnimation())} icon="pause">Pause</MiniBtn>
        <MiniBtn onClick={() => apply("Stop", r.stopGltfAnimation())} icon="stop">Stop</MiniBtn>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-16 shrink-0 text-[11px] text-ed-text-3">Speed</span>
        <CompactSlider value={state.speed} min={0.1} max={4} step={0.1} onChange={(v) => apply("Speed", r.setGltfAnimationSpeed(v))} format={(v) => `${v.toFixed(2)}×`} />
      </div>
      <div className="flex items-center gap-2">
        <span className="w-16 shrink-0 text-[11px] text-ed-text-3">Timescale</span>
        <CompactSlider value={state.timeScale} min={-2} max={2} step={0.01} onChange={(v) => apply("Timescale", r.setGltfAnimationOptions({ timeScale: v }))} format={(v) => v.toFixed(2)} />
      </div>
      <label className="flex items-center justify-between gap-2 text-[11px] text-ed-text-2">Loop<CompactCheck checked={state.loop} onChange={(v) => apply("Loop", r.setGltfAnimationLoop(v))} /></label>
      <label className="flex items-center justify-between gap-2 text-[11px] text-ed-text-2">Autoplay on load<CompactCheck checked={state.autoplayOnLoad} onChange={(v) => apply("Autoplay on load", r.setGltfAnimationOptions({ autoplayOnLoad: v }))} /></label>
      <label className="flex items-center justify-between gap-2 text-[11px] text-ed-text-2">Animate on scroll<CompactCheck checked={state.animateOnScroll} onChange={(v) => apply("Animate on scroll", r.setGltfAnimationOptions({ animateOnScroll: v }))} /></label>
      <label className="flex items-center justify-between gap-2 text-[11px] text-ed-text-2">Animate on drag<CompactCheck checked={state.animateOnDrag} onChange={(v) => apply("Animate on drag", r.setGltfAnimationOptions({ animateOnDrag: v }))} /></label>
      <label className="flex items-center justify-between gap-2 text-[11px] text-ed-text-2">Sync max duration (dev)<CompactCheck checked={state.syncMaxDuration} onChange={(v) => apply("Sync max duration", r.setGltfAnimationOptions({ syncMaxDuration: v }))} /></label>
      {err ? <span className="text-[11px] text-ed-red">{err}</span> : null}
      <span className="text-[11px] tabular-nums text-ed-text-3">State: {state.playing ? "playing" : "stopped"} · {state.time.toFixed(2)}s / {state.duration.toFixed(2)}s · {pct}%</span>
    </div>
  );
}
